import type { ElementType, ReactNode } from 'react';

import ChevronRightBreadcrumbIcon from '../icons/ChevronRightBreadcrumbIcon';

import { Box } from '../box/box';
import { Link } from '../link/link';
import { Text } from '../text/text';

interface BreadcrumbItem {
  label: ReactNode;
  path?: string;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  ariaLabel: string;
  'data-testid'?: string;
  NavLink: ElementType;
}

export interface BreadcrumbItemProps {
  path?: string;
  children: ReactNode;
  NavLink: ElementType;
}

export const BreadcrumbItem = ({
  path,
  children,
  NavLink,
}: BreadcrumbItemProps) =>
  path ? (
    <>
      <Link
        to={path}
        NavLink={NavLink}
        // Disabling prefetch for now. I thought it would prefetch the data for immediately switching
        // but all that seems to happen is that it calls the loader on hover, then calls it again on click
        // this might be a subtle react router bug so we should revisit this later
        // prefetch="intent"
      >
        {children}
      </Link>
      <ChevronRightBreadcrumbIcon size="s" />
    </>
  ) : (
    <Text variant="body">{children}</Text>
  );

export const Breadcrumb = ({
  items,
  ariaLabel,
  'data-testid': testId,
  NavLink,
}: BreadcrumbProps) => (
  <Box
    Component="nav"
    aria-label={ariaLabel}
    data-testid={testId}
    // we use data-breadcrumbs to adjust layout styles
    data-breadcrumbs
  >
    <Box Component="ul" stack="row" spacing="baseNeg5">
      {items.map(({ label, path }) => (
        <Box
          Component="li"
          key={path || 'no-path'}
          stack="row"
          alignContent={{ y: 'center' }}
          spacing="baseNeg5"
        >
          <Breadcrumb.Item path={path} NavLink={NavLink}>
            {label}
          </Breadcrumb.Item>
        </Box>
      ))}
    </Box>
  </Box>
);

Breadcrumb.Item = BreadcrumbItem;
